import { when } from 'mobx';
import { useEffect, useMemo, useState } from 'react';

import type { AggregatedStats, IAggregatedStats, IDailyStats } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import useDashboardStats from '../../../hooks/useDashboardStats';
import type { TDateRange } from '../ReportsConversionsDashboardPage.utils';
import { calcPriorPeriod } from './KpiTiles.utils';

/**
 * LOCAL TESTING AID — DO NOT COMMIT WITH ANYTHING BUT `'off'`.
 *
 * The BE doesn't return per-attribution `conversions[attr]` rollups for the
 * account model yet (see docs/conversions-dashboard-attribution-gap.md). To
 * smoke-test the delta arrows, change this to `'up'` or `'down'`:
 *
 *   'off'  → real data path (default, ship this)
 *   'up'   → current > prior  → ▲ green arrow
 *   'down' → current < prior  → ▼ red arrow
 */
const DRY_TEST_DELTA: 'off' | 'up' | 'down' = 'off';

function makeMock(num: number, roi: number, uniqueConverters: number): IAggregatedStats {
  const perAttr = { num, roi };
  return {
    num_persons_converted: uniqueConverters,
    conversions: {
      full: perAttr,
      linear: perAttr,
      positional: perAttr,
      temporal: perAttr,
      first_touch: perAttr,
      last_touch: perAttr,
    },
  } as IAggregatedStats;
}

function buildMockKpiStats(): { current: IAggregatedStats; prior: IAggregatedStats } | null {
  if (DRY_TEST_DELTA === 'up') {
    return { current: makeMock(250, 4500, 180), prior: makeMock(180, 3200, 140) };
  }
  if (DRY_TEST_DELTA === 'down') {
    return { current: makeMock(180, 3200, 140), prior: makeMock(250, 4500, 180) };
  }
  return null;
}

const MOCK_KPI_STATS = buildMockKpiStats();

export interface IUseKpiStatsParams {
  range: TDateRange;
}

export interface IUseKpiStatsReturn {
  currentStats?: IAggregatedStats;
  priorStats?: IAggregatedStats;
  isCurrentLoading: boolean;
  isPriorLoading: boolean;
  hasCurrentError: boolean;
  hasPriorError: boolean;
  /** Exposed so trend tiles can label prior-period series correctly. */
  priorRange: TDateRange;
}

interface IRangeFetchResult {
  stats?: IAggregatedStats;
  hasError: boolean;
}

async function fetchRangeStats(
  AggregatedStats: AggregatedStats,
  range: TDateRange,
): Promise<IRangeFetchResult> {
  try {
    const stats = AggregatedStats.aggregate({
      filters: {
        metadata__date__gte: range[0],
        metadata__date__lte: range[1],
      },
      model: 'account',
    });
    await when(() => !stats.isPending);
    return { stats: stats.toJS(), hasError: false };
  } catch {
    return { stats: undefined, hasError: true };
  }
}

/**
 * Fetches aggregated conversion stats for both the current and prior period.
 * Each period is fetched independently so one failure does not block the other.
 */
export function useKpiStats({ range }: IUseKpiStatsParams): IUseKpiStatsReturn {
  const { AggregatedStats } = useStore();

  const priorRange = useMemo(() => calcPriorPeriod(range), [range]);

  const [currentStats, setCurrentStats] = useState<IAggregatedStats>();
  const [priorStats, setPriorStats] = useState<IAggregatedStats>();
  const [isCurrentLoading, setIsCurrentLoading] = useState(false);
  const [isPriorLoading, setIsPriorLoading] = useState(false);
  const [hasCurrentError, setHasCurrentError] = useState(false);
  const [hasPriorError, setHasPriorError] = useState(false);

  useEffect(() => {
    setIsCurrentLoading(true);
    setHasCurrentError(false);
    void fetchRangeStats(AggregatedStats, range).then(({ stats, hasError }) => {
      setCurrentStats(stats);
      setHasCurrentError(hasError);
      setIsCurrentLoading(false);
    });
  }, [range]);

  useEffect(() => {
    setIsPriorLoading(true);
    setHasPriorError(false);
    void fetchRangeStats(AggregatedStats, priorRange).then(({ stats, hasError }) => {
      setPriorStats(stats);
      setHasPriorError(hasError);
      setIsPriorLoading(false);
    });
  }, [priorRange]);

  if (MOCK_KPI_STATS) {
    return {
      currentStats: MOCK_KPI_STATS.current,
      priorStats: MOCK_KPI_STATS.prior,
      isCurrentLoading: false,
      isPriorLoading: false,
      hasCurrentError: false,
      hasPriorError: false,
      priorRange,
    };
  }

  return {
    currentStats,
    priorStats,
    isCurrentLoading,
    isPriorLoading,
    hasCurrentError,
    hasPriorError,
    priorRange,
  };
}

export interface IUseConversionsTrendParams {
  range: TDateRange;
  priorRange: TDateRange;
}

export interface IUseConversionsTrendReturn {
  currentData: IDailyStats[];
  priorData: IDailyStats[];
  isLoading: boolean;
}

/**
 * Fetches daily conversion time-series for both the current and prior period,
 * returning two series for the dual-line trend chart.
 */
export function useConversionsTrend({
  range,
  priorRange,
}: IUseConversionsTrendParams): IUseConversionsTrendReturn {
  const { isLoading: isCurrentLoading, chartData: currentData } = useDashboardStats({
    timeFrameValue: range,
  });

  const { isLoading: isPriorLoading, chartData: priorData } = useDashboardStats({
    timeFrameValue: priorRange,
  });

  return {
    currentData,
    priorData,
    isLoading: isCurrentLoading || isPriorLoading,
  };
}
